import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import CreatePostFetchJSON from "../../ServerCalls/CreatePostFetchJSON";

/**
 * Modal: one row per locality — readonly locality on the left, avifauna site dropdown on the right.
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} fileName
 * @param {string|number} fileYear
 */
const AvifaunaLocalityMappingModal = ({
  open,
  onClose,
  fileName,
  fileYear,
  onNotify,
}) => {
  const postBuilder = CreatePostFetchJSON();
  const [localities, setLocalities] = useState([]);
  const [avifaunaSites, setAvifaunaSites] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState("");
  const [avifaunaByLocalityId, setAvifaunaByLocalityId] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubmitError("");
    const fetchMetadata = async () => {
      if (!fileName || !fileYear) {
        setMetadataError("Missing file details for locality mapping.");
        return;
      }
      setMetadataLoading(true);
      setMetadataError("");
      try {
        const response = await postBuilder.caller("/getLocalityMappingMetadata", {
          file_name: fileName,
          file_year: fileYear,
        });
        const parsed = await response.json();
        if (!response.ok) {
          setMetadataError(parsed?.error || "Failed to load mapping metadata.");
          setLocalities([]);
          setAvifaunaSites([]);
          return;
        }
        const existingLocalities = (parsed?.data?.existing_localities || []).map((loc) => ({
          id: loc?.id,
          name: loc?.name || String(loc?.id ?? ""),
        }));
        const avifaunaSiteNames = (parsed?.data?.avifauna_site_names || []).map((site) => ({
          id: site?.id,
          name: site?.name || site?.location_name || "",
          latitude: site?.latitude ?? site?.Latitude,
          longitude: site?.longitude ?? site?.Longitude,
        }));
        setLocalities(existingLocalities);
        setAvifaunaSites(avifaunaSiteNames);
        const initial = {};
        existingLocalities.forEach((loc) => {
          initial[loc.id] = "";
        });
        setAvifaunaByLocalityId(initial);
      } catch (error) {
        setMetadataError("Something went wrong while loading locality metadata.");
        setLocalities([]);
        setAvifaunaSites([]);
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, [open, fileName, fileYear]);

  const handleAvifaunaChange = (localityId) => (_, newValue) => {
    setAvifaunaByLocalityId((prev) => ({
      ...prev,
      [localityId]: newValue ? String(newValue.id) : "",
    }));
  };

  const handleSubmit = async () => {
    if (!localities.length) {
      setSubmitError("No localities to map.");
      return;
    }
    const missing = localities.some((loc) => !avifaunaByLocalityId[loc.id]);
    if (missing) {
      setSubmitError("Please select an avifauna site for every locality.");
      return;
    }
    setSubmitLoading(true);
    setSubmitError("");
    try {
      const response = await postBuilder.caller("/generate_avifauna_template", {
        file_name: fileName,
        file_year: fileYear,
        locality_mapping: localities.reduce((acc, loc) => {
          const siteId = avifaunaByLocalityId[loc.id];
          const site = avifaunaSites.find((s) => String(s.id) === String(siteId));
          const key = String(loc.id);
          acc[key] = {
            locality_id: loc.id,
            avifauna_site_id: siteId,
            avifauna_site_name: site?.name ?? "",
            avifauna_latitude: site?.latitude ?? "",
            avifauna_longitude: site?.longitude ?? "",
          };
          return acc;
        }, {}),
      });
      const contentType = response.headers.get("content-type") || "";
      if (response.ok && contentType.includes("csv")) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${String(fileName).replace(/\.jpg$/i, "")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        onNotify?.(true, "File Downloaded", "success");
        onClose();
        return;
      }
      let errBody = {};
      try {
        errBody = await response.json();
      } catch {
        /* not JSON */
      }
      const errorMessage = errBody?.error || "Failed to generate avifauna template.";
      setSubmitError(errorMessage);
      onNotify?.(true, errorMessage, "error");
    } catch {
      const errorMessage = "Failed to generate avifauna template.";
      setSubmitError(errorMessage);
      onNotify?.(true, errorMessage, "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="avifauna-locality-mapping-title"
    >
      <DialogTitle
        id="avifauna-locality-mapping-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Map localities to avifauna sites
        <IconButton aria-label="close" onClick={onClose} size="small">
          <MdClose size={22} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each locality is shown on the left. Choose the matching avifauna site on the right.
        </Typography>
        {metadataError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {metadataError}
          </Typography>
        )}
        {submitError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {submitError}
          </Typography>
        )}
        {metadataLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading locality mapping metadata...
          </Typography>
        )}

        <Grid container spacing={1} sx={{ mb: 1, display: { xs: "none", sm: "flex" } }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Locality
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Avifauna site
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {!metadataLoading &&
            localities.map((loc) => (
            <Grid container spacing={2} key={loc.id} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Locality"
                  value={loc.name}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={avifaunaSites}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  value={
                    avifaunaSites.find(
                      (s) => String(s.id) === (avifaunaByLocalityId[loc.id] ?? "")
                    ) || null
                  }
                  onChange={handleAvifaunaChange(loc.id)}
                  ListboxProps={{ style: { maxHeight: 280 } }}
                  noOptionsText="No matching sites"
                  componentsProps={{
                    popper: {
                      sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Avifauna site" placeholder="Search or select…" />
                  )}
                />
              </Grid>
            </Grid>
            ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={metadataLoading || submitLoading}
        >
          {submitLoading ? "Generating…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvifaunaLocalityMappingModal;
